import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { getToken, removeToken, setToken } from '../utils/auth';
import {
  ApiResponse,
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  User
} from '../types/auth';


export interface UserManagement {
  id: string;
  email: string;
  displayName?: string;
  roles: string[];
  isGoogleAccount: boolean;
  isMicrosoftAccount: boolean;
  createdUtc: string;
}

// Create axios instance with base configuration
// Use absolute VITE_API_BASE only if it is an absolute URL; otherwise fall back to current origin.
const configuredBase = (import.meta as any).env?.VITE_API_BASE as string | undefined;
const resolvedBaseURL = configuredBase && /^https?:\/\//i.test(configuredBase)
  ? configuredBase
  : window.location.origin;

const api: AxiosInstance = axios.create({
  baseURL: resolvedBaseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Auth state flags for smart 401 handling ---
// URLs that should NOT trigger auto-logout on 401
const AUTH_ENDPOINTS = ['/api/auth/login', '/api/auth/register', '/api/auth/refresh'];
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];
// Debounce: prevent multiple session-expired events within a short window
let sessionExpiredFired = false;
let sessionExpiredTimer: ReturnType<typeof setTimeout> | null = null;

function onTokenRefreshed(newToken: string) {
  refreshSubscribers.forEach(cb => cb(newToken));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function sendAuthAudit(reason: string, detail?: string) {
  try {
    // Parse token to extract email & expiry for diagnostics (fire-and-forget)
    const token = getToken();
    let email: string | undefined;
    let tokenExpiry: string | undefined;
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        email = payload.email || payload.unique_name;
        tokenExpiry = payload.exp ? new Date(payload.exp * 1000).toISOString() : undefined;
      } catch { /* ignore parse errors */ }
    }
    const baseURL = api.defaults.baseURL || '';
    fetch(`${baseURL}/api/auth/audit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason, detail, email, tokenExpiry }),
    }).catch(() => { /* best-effort, ignore network errors */ });
  } catch { /* best-effort */ }
}

function fireSessionExpired(detail?: string) {
  if (sessionExpiredFired) return; // already fired recently
  sessionExpiredFired = true;
  sendAuthAudit('SESSION_EXPIRED_401', detail || 'refresh_exhausted');
  removeToken();
  window.dispatchEvent(new Event('auth:session-expired'));
  // Reset debounce after 2 seconds
  if (sessionExpiredTimer) clearTimeout(sessionExpiredTimer);
  sessionExpiredTimer = setTimeout(() => { sessionExpiredFired = false; }, 2000);
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors with smart refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';

    // Don't handle 401 for auth endpoints (login, register, refresh)
    if (error.response?.status === 401 && !AUTH_ENDPOINTS.some(ep => requestUrl.includes(ep))) {
      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      // Check if we have a token that might still be refreshable
      // (the refresh endpoint requires [Authorize], so it only works if the server
      // still considers the token valid within its ClockSkew window)
      const currentToken = getToken();
      if (currentToken && !originalRequest._retried) {
        originalRequest._retried = true;
        isRefreshing = true;

        try {
          const response = await api.post('/api/auth/refresh');
          if (response.data?.success && response.data?.data?.token) {
            const newToken = response.data.data.token;
            setToken(newToken);
            isRefreshing = false;
            onTokenRefreshed(newToken);
            // Notify AuthContext about the new token
            window.dispatchEvent(new CustomEvent('auth:token-refreshed', { detail: { token: newToken } }));
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          }
        } catch {
          // Refresh failed – token is truly invalid
        }
        isRefreshing = false;
        refreshSubscribers = [];
      }

      // All recovery attempts failed – fire session expired (debounced)
      fireSessionExpired();
    }
    return Promise.reject(error);
  }
);

// Auth API endpoints
export const authApi = {
  /**
   * Register a new user
   */
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    // Send password as plain text - backend handles encryption/decryption
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await api.post('/api/auth/register', data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Registration failed');
    }
    return response.data.data!;
  },

  /**
   * Login with email and password
   */
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    // Send password as plain text - backend handles encryption/decryption
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await api.post('/api/auth/login', data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Login failed');
    }
    return response.data.data!;
  },

  /**
   * Get current user profile
   */
  getProfile: async (): Promise<User> => {
    const response: AxiosResponse<ApiResponse<User>> = await api.get('/api/auth/profile');
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get profile');
    }
    return response.data.data!;
  },

  /**
   * Initiate Google OAuth login
   */
  googleLogin: (): void => {
    window.location.href = `${api.defaults.baseURL}/api/auth/google`;
  },

  /**
   * Initiate Microsoft OAuth login
   * Note: Must go directly to backend (not through /api/ proxy) to preserve OAuth cookies
   */
  microsoftLogin: (): void => {
    // Dynamically determine backend URL based on current environment
    const currentOrigin = window.location.origin;

    // Check if VITE_BACKEND_URL is configured (for custom domains)
    const configuredBackendUrl = (import.meta as any).env?.VITE_BACKEND_URL as string | undefined;

    let backendUrl: string;

    if (configuredBackendUrl) {
      // Use explicitly configured backend URL (for custom domains)
      backendUrl = configuredBackendUrl;
    } else if (currentOrigin.includes('localhost')) {
      // If running on localhost, use local backend on port 8080
      backendUrl = 'http://localhost:8080';
    } else {
      // Default: Replace 'vp-frontend' with 'vp-backend' in the URL
      backendUrl = currentOrigin.replace('vp-frontend', 'vp-backend');
    }

    // Add frontend URL as query parameter so backend knows where to redirect
    const frontendUrl = encodeURIComponent(currentOrigin);
    window.location.href = `${backendUrl}/api/auth/microsoft?frontend=${frontendUrl}`;
  },

  /**
   * Update user roles (Admin only)
   */
  updateUserRoles: async (userId: string, roles: string[]): Promise<User> => {
    const response: AxiosResponse<ApiResponse<User>> = await api.put('/api/auth/roles', {
      userId,
      roles
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update user roles');
    }
    return response.data.data!;
  },

  /**
   * Get all users for admin management (Admin only)
   */
  getAllUsers: async (): Promise<UserManagement[]> => {
    const response: AxiosResponse<ApiResponse<UserManagement[]>> = await api.get('/api/auth/users');
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get users');
    }
    return response.data.data!;
  },

  /**
   * Create a Google user manually (Admin only)
   */
  createGoogleUser: async (email: string, displayName?: string): Promise<AuthResponse> => {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await api.post('/api/auth/create-google-user', {
      email,
      displayName
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create Google user');
    }
    return response.data.data!;
  },

  /**
   * Create a native user manually (Admin only)
   */
  createNativeUser: async (email: string, password: string, displayName?: string): Promise<AuthResponse> => {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await api.post('/api/auth/create-native-user', {
      email,
      password: password, // Send password as plain text
      displayName
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create native user');
    }
    return response.data.data!;
  },

  /**
   * Reset user password (Admin only)
   */
  resetUserPassword: async (userId: string, newPassword: string): Promise<void> => {
    const response: AxiosResponse<ApiResponse<object>> = await api.post('/api/auth/reset-password', {
      userId,
      newPassword
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to reset password');
    }
  },

  /**
   * Delete user (Admin only)
   */
  deleteUser: async (userId: string): Promise<void> => {
    const response: AxiosResponse<ApiResponse<object>> = await api.delete(`/api/auth/users/${userId}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete user');
    }
  },

  /**
   * Refresh JWT token (returns a new token with extended expiry)
   */
  refreshToken: async (): Promise<AuthResponse> => {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await api.post('/api/auth/refresh');
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to refresh token');
    }
    return response.data.data!;
  },
};

// Data Management API
export const dataManagementApi = {
  /**
   * Get list of all available tables for data management
   */
  getTables: async (): Promise<any[]> => {
    const response: AxiosResponse<any[]> = await api.get('/api/datamanagement/tables');
    return response.data;
  },

  /**
   * Download CSV template for a specific table
   */
  downloadTemplate: async (tableName: string): Promise<Blob> => {
    const response = await api.get(`/api/datamanagement/template/${tableName}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Export data from a specific table as CSV
   */
  exportData: async (tableName: string): Promise<Blob> => {
    const response = await api.get(`/api/datamanagement/export/${tableName}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Import data from CSV file to a specific table
   */
  importData: async (tableName: string, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);

    const response: AxiosResponse<ApiResponse<any>> = await api.post(
      `/api/datamanagement/import/${tableName}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to import data');
    }
    return response.data.data!;
  }
};



// Health check
export const healthApi = {
  /**
   * Check API health
   */
  check: async (): Promise<any> => {
    const response: AxiosResponse<ApiResponse> = await api.get('/health');
    return response.data;
  },
};

export { sendAuthAudit };
export default api;
